import CustomTooltip from 'components/Tooltip';
import { IChartData } from 'interfaces/chartData';
import 'react-tooltip/dist/react-tooltip.css';
import { getPreviousQuarter } from 'utils/helpers';
import logoIcon from 'assets/images/iconiq-logo.svg';
import logoWhiteIcon from 'assets/images/iconiq-logo-white.svg';
import ChartHeader from './ChartHeader';
import Resources from './Resources/Resources';
import SocialIcons from './SocialIcons';
import infoIcon from 'assets/icons/info.svg';
import infoWhiteIcon from 'assets/icons/info-white.svg';
import ChartOptions from './ChartOptions';

interface ChartContainerProps {
  title: string;
  tooltip: string;
  children: React.ReactNode;
  chartData: IChartData[];
  prevChart: string;
  nextChart: string;
  currentChartIndex: number;
  handleChartIndex: (newChartIndex: number) => void;
}

export const chartsToHideAvgDealSizeSwitch = [
  'YoY ARR Growth',
  'Rule of 40',
  'Burn Multiple',
  'OpEx as a % of Revenue',
  'Gross Margin',
  'FCF Margin',
  'Headcount Distribution',
  'ARR per FTE',
  'OpEx per FTE'
];

const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  children,
  tooltip,
  prevChart,
  nextChart,
  handleChartIndex,
  currentChartIndex
}) => {
  const formulas: { [key: string]: any } = {
    'YoY ARR Growth': 'Year 2 ARR / Year 1 ARR - 1',
    'Net Dollar Retention':
      '1 + [(Net New ARR - New Logo ARR) / AVERAGE(BOP ARR, EOP ARR)]',
    'Gross Dollar Retention':
      '1 + [(Gross Churn ARR) / AVERAGE(BOP ARR, EOP ARR)]',
    'Net Magic Number': 'Net New ARR / S&M Expense',
    'Gross Margin': 'Gross Profit / Revenue',
    'Rule of 40': 'ARR Growth YoY % + FCF Margin %',
    'Burn Multiple': 'Net Burn / Net New ARR',
    'ARR per FTE': 'ARR / Total Headcount',
    'OpEx as a % of Revenue': (
      <>
        <span className='underline'>Median</span> OpEx by Function / Revenue
      </>
    ),
    'FCF Margin': 'FCF / Revenue',
    'Quick Ratio': 'Gross New ARR / Gross Churned ARR',
    'Headcount Distribution': (
      <>
        <span className='underline'>Average</span> Headcount by Function / Total
        Company Headcount (excluding Cost of Revenue FTEs)
      </>
    ),
    'Gross New ARR Composition': (
      <>
        ARR New Logo: New Logo ARR / Gross New ARR
        <br />
        ARR Expansion: Expansion ARR / Gross New ARR
      </>
    ),
    'New Logo Velocity': '[New Logo ARR t=0 / New Logo ARR t-1] -1',
    'CAC Payback': 'CAC / (ARR Per Customer & Gross Margin)',
    'OpEx per FTE': 'Total OpEx / Total Employees'
  };

  const chartsToHideQuartileMedianSwitch = [
    'Headcount Distribution',
    'OpEx as a % of Revenue',
    'Gross New ARR Composition'
  ];

  const chartsWithMultilineFormulas = ['Gross New ARR Composition'];

  const isQuartileMedianSwitchDisabled = chartsToHideQuartileMedianSwitch.some(
    label => label === title
  );

  const isAvgDealSizeSwitchDisabled = chartsToHideAvgDealSizeSwitch.some(
    label => label === title
  );

  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  return (
    <>
      <div className='flex justify-between items-center'>
        <ChartHeader
          title={title}
          tooltip={tooltip}
          prevChart={prevChart}
          nextChart={nextChart}
          handleChartIndex={handleChartIndex}
          currentChartIndex={currentChartIndex}
        />
      </div>

      <ChartOptions
        title={title}
        isAvgDealSizeSwitchDisabled={isAvgDealSizeSwitchDisabled}
        isQuartileMedianSwitchDisabled={isQuartileMedianSwitchDisabled}
      />

      <div className='w-full mt-[10px] relative'>
        {children}
        <SocialIcons />
      </div>

      <hr className='mt-4 dark:border-gray-850' />

      <div className='flex justify-between items-center my-4 gap-4'>
        <div className='flex-1 flex items-start gap-2'>
          {formulas[title] ? (
            <>
              <div className='flex items-start gap-2 text-sm font-light text-black dark:text-white'>
                <span
                  className={
                    chartsWithMultilineFormulas.some(val => val === title)
                      ? 'w-min'
                      : ''
                  }
                >
                  Formula:
                </span>
                <span className='block flex-1 whitespace-nowrap'>
                  {formulas[title]}
                </span>
              </div>

              <div className='w-[1px] h-4 bg-gray-300 dark:bg-[#404040]' />
            </>
          ) : undefined}

          <span className='text-sm font-light text-black dark:text-white'>
            {`Data last refreshed as of ${getPreviousQuarter()}`}
          </span>

          <div className='w-[1px] h-4 bg-gray-300 dark:bg-[#404040]' />

          <div className='flex items-start gap-1'>
            <CustomTooltip
              id='chartInfo'
              content="The numbers, figures and case studies included in this tool have been included for purposes of illustration only, and no assurance can be given that the actual results achieved by any ICONIQ portfolio or any of ICONIQ's partners or affiliates will correspond with the results contemplated in the tool. Past performance is not a reliable indicator of future performance, and any investment may result in the loss of all or some of the amounts invested."
              icon={isDarkMode ? infoWhiteIcon : infoIcon}
            />
            <span className='text-sm font-light text-black dark:text-white'>
              Disclaimers
            </span>
          </div>
        </div>

        <div className='w-fit'>
          <img alt='ICONIQ Logo' src={isDarkMode ? logoWhiteIcon : logoIcon} />
        </div>
      </div>
      <Resources />
    </>
  );
};

export default ChartContainer;
